import React from 'react';
import { ArrowLeft, Trophy, ChevronRight, Clock, CheckCircle, Lock, Star } from 'lucide-react';
import { useXp } from '../../context/XpContext';
import { ELEVE_GRADIENTS } from '../../utils/eleveTheme';

/**
 * Vue « Mes résultats » de l'élève — charte navy/cyan/orange de l'espace élève.
 * Stats (terminées / en attente / XP réel du profil) + anneau de complétion
 * + liste des évaluations complétées (correction cliquable si visible).
 * Contrats inchangés : listEvalBilan / stats / verifierSiEleveARepondu /
 * onBack / onShowCorrection.
 */

/** Anneau SVG de progression (complétion des quiz), animé en CSS. */
const AnneauCompletion = ({ pct }) => {
  const r = 30;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative w-20 h-20 flex-shrink-0">
      <svg viewBox="0 0 72 72" className="w-full h-full -rotate-90">
        <circle cx="36" cy="36" r={r} fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="7" />
        <circle
          cx="36" cy="36" r={r} fill="none" stroke="#29B6E8" strokeWidth="7" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - Math.max(0, Math.min(100, pct)) / 100)}
          style={{ transition: 'stroke-dashoffset 1s ease-out' }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-extrabold text-white" style={{ fontFamily: 'Fredoka' }}>
        {pct}%
      </span>
    </div>
  );
};

const TuileStat = ({ icone, fond, bord, label, valeur, couleur }) => (
  <div className="rounded-2xl p-4 flex items-center justify-between" style={{ background: fond, border: `1px solid ${bord}` }}>
    <div>
      <p className="text-xs font-bold" style={{ color: 'rgba(255,255,255,.6)' }}>{label}</p>
      <p className="text-3xl font-extrabold mt-1" style={{ fontFamily: 'Fredoka', color: couleur }}>{valeur}</p>
    </div>
    <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,.07)' }}>{icone}</div>
  </div>
);

const ResultatViewEleve = ({
  listEvalBilan,
  stats,
  verifierSiEleveARepondu,
  onBack,
  onShowCorrection,
}) => {
  const { profil } = useXp();
  const completees = listEvalBilan.filter(e => verifierSiEleveARepondu(e));

  return (
    <div className="eleve-gamified min-h-screen p-5 sm:p-6 pb-24 relative overflow-hidden" style={{ background: ELEVE_GRADIENTS.fond }}>
      {/* Halos discrets (recette passation) */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -left-16 w-96 h-96 rounded-full blur-3xl" style={{ background: 'rgba(41,182,232,.10)' }} />
        <div className="absolute bottom-0 -right-16 w-80 h-80 rounded-full blur-3xl" style={{ background: 'rgba(249,115,22,.08)' }} />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* En-tête */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={onBack}
            aria-label="Retour"
            className="p-3 rounded-xl transition hover:opacity-80"
            style={{ background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.14)' }}
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-2" style={{ fontFamily: 'Fredoka' }}>
              📊 Mes résultats
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,.6)' }}>Tes performances en un coup d'œil</p>
          </div>
        </div>

        {/* Stats : 3 tuiles + anneau de complétion */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <TuileStat
            label="Quiz terminés"
            valeur={stats.completed}
            couleur="#4ADE80"
            fond="rgba(31,157,85,.10)" bord="rgba(31,157,85,.35)"
            icone={<CheckCircle className="w-7 h-7" style={{ color: '#4ADE80' }} />}
          />
          <TuileStat
            label="En attente"
            valeur={stats.pending}
            couleur="#FFB938"
            fond="rgba(255,185,56,.08)" bord="rgba(255,185,56,.32)"
            icone={<Clock className="w-7 h-7" style={{ color: '#FFB938' }} />}
          />
          <TuileStat
            label="XP total"
            valeur={`${profil?.xp_total ?? 0} XP`}
            couleur="#29B6E8"
            fond="rgba(41,182,232,.08)" bord="rgba(41,182,232,.35)"
            icone={<Star className="w-7 h-7" style={{ color: '#29B6E8' }} />}
          />
        </div>

        {/* Anneau de complétion (part des quiz donnés que tu as terminés) */}
        <div className="rounded-2xl p-4 mb-6 flex items-center gap-4" style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(201,223,240,.16)' }}>
          <AnneauCompletion pct={stats.successRate} />
          <div>
            <p className="text-white font-bold" style={{ fontFamily: 'Fredoka' }}>Ta progression</p>
            <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,.65)' }}>
              {stats.total > 0
                ? `Tu as terminé ${stats.completed} quiz sur ${stats.total} — continue comme ça !`
                : 'Aucun quiz pour le moment. Reviens vite !'}
            </p>
          </div>
        </div>

        {/* Liste des évaluations complétées */}
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(201,223,240,.16)' }}>
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Fredoka' }}>
            <Trophy className="w-5 h-5" style={{ color: '#FFB938' }} />
            Évaluations complétées
          </h2>

          {completees.length > 0 ? (
            <div className="space-y-2.5">
              {completees.map((e, index) => (
                <div
                  key={index}
                  // Le titre entier en info-bulle : la ligne le coupe (demande du prof, 2026-10-04).
                  title={e.titre}
                  className={`flex items-center justify-between p-3.5 rounded-xl transition-all ${e.correction_visible ? 'cursor-pointer hover:translate-x-1' : 'cursor-default'}`}
                  style={{
                    background: e.correction_visible ? 'rgba(41,182,232,.10)' : 'rgba(255,255,255,.04)',
                    border: `1px solid ${e.correction_visible ? 'rgba(41,182,232,.3)' : 'rgba(255,255,255,.08)'}`,
                  }}
                  onClick={() => e.correction_visible && onShowCorrection(e)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: e.correction_visible ? 'rgba(31,157,85,.2)' : 'rgba(255,255,255,.06)' }}
                    >
                      {e.correction_visible
                        ? <CheckCircle className="w-5 h-5" style={{ color: '#4ADE80' }} />
                        : <Lock className="w-4 h-4" style={{ color: 'rgba(255,255,255,.4)' }} />}
                    </div>
                    <div className="min-w-0">
                      <span className="text-white font-semibold block truncate">{e.titre}</span>
                      {!e.correction_visible && (
                        <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,.45)' }}>Correction pas encore disponible</p>
                      )}
                    </div>
                  </div>
                  {e.correction_visible
                    ? <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: '#29B6E8' }} />
                    : <span className="text-xs italic flex-shrink-0" style={{ color: 'rgba(255,255,255,.35)' }}>Bientôt</span>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-8 text-sm" style={{ color: 'rgba(255,255,255,.5)' }}>
              Aucune évaluation terminée pour le moment
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultatViewEleve;
